import Translator from 'app/components/common/Translator';
import { de } from 'date-fns/locale';
import { Translate } from 'infrastructure/mapping/Translate';
import { LoginUser } from 'models/LoginUser';
import { BasicDtaObject, FormElement, SelectConfig } from 'models/ModalData';
import { StoreModel } from 'models/StoreModel';
import * as React from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import Select from 'react-select';
registerLocale('de', de);

interface IProps {
    formObject: object | undefined;
    nonSelectables?: string[];
    optionals?: string[];
    template?: object; // typed empty object, gives the input type for fields that are null
    hidden?: string[];
    protected?: string[];
    validations?: IValidation[];
    toolTips?: Tooltip[];
    dependencies?: IDependency[];
    inheritances?: IInheritances[];
    selectConfigs?: SelectConfig[];
    labels?: { divId: string, text: (formObject: object) => string }[];
    loginUser: LoginUser;
    callback: Function;
    // callback takes only the form object, even if a user is logged in (account pages reached via mail links)
    anonymous?: boolean;
    store: StoreModel;
}

export interface IValidation {
    divId: string,
    compareValues?: boolean,
    storeObject?: boolean,
    compareToObject?: string,
    compareToAttribute?: string,
    validation: (a: ValidationTypes, b: ValidationTypes | undefined) => boolean,
    errorMsg: string
}

export type Tooltip = {
    divId: string,
    text: string
}

export interface IDependency {
    label: string,
    dependentOn: string
}

export interface IInheritances {
    label: string,
    trigger: string,
    valueFrom: string
}

interface ValidationError {
    divId: string,
    errorMsg: string
}


export type ValidationTypes = number | string | Date | boolean | object;

interface IState {
    selectedOptions: SelectedOption[];
    selectedCheckBoxes: string[];
    formObject: object | undefined;
    validationErrors: ValidationError[];
}
type SelectedOption = {
    divId: string,
    label?: string,
    value?: BasicDtaObject
}

const InputTypes = {
    basic: 'basic',
    checkbox: 'checkbox',
    date: 'date',
    select: 'select',
    array: 'array',
    radioArray: 'radioArray',
    newObject: 'form-group',
    password: 'password'
}

export class ReactForm extends React.Component<IProps, IState> {
    public readonly state: IState = {
        selectedOptions: this.setInitialSelectOptions(),
        selectedCheckBoxes: this.setInitialCheckBoxes(),
        formObject: this.props.formObject,
        validationErrors: []
    }

    loggedIn() {
        return this.props.loginUser && !!this.props.loginUser.token;
    }

    setInitialSelectOptions(): SelectedOption[] {
        let initialState: SelectedOption[] = [];
        const fos = this.getFormElements(this.props.formObject, this.props.template);

        initialState = fos.filter(fo => fo.type === 'object' && !this.props.nonSelectables?.includes(fo.label) && fo.value).map(fo => {
            return {
                divId: fo.label,
                label: this.optionLabel(fo.label, fo.value as BasicDtaObject),
                value: fo.value as BasicDtaObject
            }
        })

        let add: SelectedOption[] = [];
        fos.filter(fo => fo.type === 'object' && this.props.nonSelectables?.includes(fo.label) && fo.value).map(fo => {
            const subFos = this.getFormElements(fo.value as object);
            add.push(
                ...subFos.filter(sf => sf.type === 'object' && sf.value).map(sf => {
                    return {
                        divId: fo.label + "." + sf.label,
                        label: (sf.value as BasicDtaObject).name,
                        value: sf.value as BasicDtaObject
                    }
                })
            )
        })
        initialState.push(...add);
        return initialState;
    }

    setInitialCheckBoxes() {
        let selectedCheckBoxes: string[] = [];
        const fos = this.getFormElements(this.props.formObject, this.props.template).filter(fo => fo.type === "radioArray");
        if (fos) {
            fos.forEach(fo => {
                const radioArray = fo.value as BasicDtaObject[];
                for (const ra of radioArray) {
                    if (this.getValueFromObj(ra, "checked")) {
                        selectedCheckBoxes.push(fo.label + "." + ra.id)
                    }
                }
            })
        }
        return selectedCheckBoxes;
    }

    getFormElements(formObject: object | undefined, template?: object) {
        let formElements: FormElement<string | number | object | Date | object[]>[] = [];
        if (formObject) {
            for (const [key, value] of Object.entries(formObject)) {
                if (key !== "errors") {
                    // a null value has no type of its own, take it from the template
                    // without a template value keep the old behaviour (e.g. archivedDate: null renders nothing)
                    const typed = value ?? (template as { [key: string]: unknown } | undefined)?.[key] ?? value;
                    let type: string = typeof typed;
                    if (typed instanceof Array)
                        if (this.props.nonSelectables?.includes(key))
                            type = "array"
                        else
                            type = "radioArray"
                    if (typed instanceof Date)
                        type = "date";
                    if (key.toUpperCase().includes("PASSWORD") && type === "string")
                        type = "password"
                    const isRequired = !(this.props.optionals && this.props.optionals.includes(key))
                    formElements.push({ label: key, type: type, required: isRequired, value: value });
                }
            }
        }
        return formElements
    }

    performCallback(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.formObject && this.props.callback) {
            if (this.loggedIn() && !this.props.anonymous) {
                this.props.callback(this.props.loginUser.token, this.filterFormObject(this.state.formObject))
            } else {
                this.props.callback(this.state.formObject);
            }
        }
    }

    filterFormObject(obj: object) {
        const fes = this.getFormElements(obj, this.props.template);
        let retFo = Object.assign({}, obj);
        fes.filter(fe => fe.type === "radioArray").forEach(fe => {
            let raVal = fe.value as Array<BasicDtaObject>;
            let filteredRaVal = raVal.filter(rv => this.state.selectedCheckBoxes.includes(fe.label + "." + rv.id));
            retFo = this.setValueOnObject(retFo, filteredRaVal, fe.label);
        });
        // empty optional fields are sent as null
        fes.filter(fe => !fe.required && this.isEmpty(fe.value)).forEach(fe => {
            retFo = this.setValueOnObject(retFo, null, fe.label);
        });

        return retFo;
    }

    isEmpty(value: unknown) {
        return value === null || value === undefined || value === ''
            || (typeof value === 'number' && isNaN(value))
            || (typeof value === 'object' && !(value instanceof Date) && !(value instanceof Array) && (value as BasicDtaObject).id === 0);
    }

    inputTypeForField(ft: string) {
        switch (ft) {
            case 'number': return 'number'
            case 'password': return 'password'
            default: return 'text'
        }
    }

    checkForValidationError(divId: string, val: ValidationTypes): undefined | string {
        if (this.props.validations) {
            const validation = this.props.validations.find(v => v.divId === divId);
            if (validation) {
                var compareVal: ValidationTypes | undefined;
                if (validation.compareValues && validation.compareToObject && validation.compareToAttribute) {
                    if (validation.storeObject) {
                        compareVal = this.getValueFromStore(validation.compareToObject, validation.compareToAttribute)
                    } else {
                        compareVal = this.getValueFromState(validation.compareToObject + "." + validation.compareToAttribute)
                    }
                }
                return validation.validation(val, compareVal) ? undefined : validation.errorMsg;
            }
        }
        return undefined;
    }

    getValueFromStore(object: string, attribute: string) {
        for (const [key, value] of Object.entries(this.props.store)) {
            if (key === (object + 's')) { // looking for arrays, so plural
                for (const [key, attr] of Object.entries(value[0])) {
                    if (key === (attribute)) {
                        return attr as ValidationTypes
                    }
                }
            }
        }
    }

    setValueOnState(newValue: string | number | BasicDtaObject | object | Date | boolean | null, _fieldName: string) {
        let fieldName = "";
        let prefix = ""
        let idx = -1;
        if ((_fieldName.match(/./g) || []).length < 2) {
            fieldName = _fieldName.indexOf(".") > 0 ? _fieldName.substr(_fieldName.indexOf(".") + 1) : _fieldName;
            prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
        } else {
            prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
            let len = prefix.length + 1;
            idx = parseInt(_fieldName.indexOf(".", len) > 0 ? _fieldName.substr(len, _fieldName.indexOf(".", len)) : "-1");
            let len2 = _fieldName.indexOf(".", len);
            fieldName = _fieldName.indexOf(".", len2) > 0 ? _fieldName.substr(_fieldName.indexOf(".", len2) + 1) : _fieldName;
        }

        let obj = this.state.formObject!;
        if (prefix) { // name refers to a sublevel, retrieve it
            obj = this.getValueFromObj(obj, prefix, idx) as object;
        }
        let formData = this.setValueOnObject(obj, newValue, fieldName);
        if (prefix) { // name refers to a sublevel, retrieve it
            formData = this.setValueOnObject(this.state.formObject!, formData, prefix, idx);
        }
        this.setState({
            formObject: formData
        });
    }

    onExit(e: React.FormEvent<HTMLInputElement>, _fieldName: string) {
        const newValue = e.currentTarget.value;
        let fieldName = _fieldName.indexOf(".") > 0 ? _fieldName.substr(_fieldName.indexOf(".") + 1) : _fieldName;
        const validationError = this.checkForValidationError(fieldName, newValue);
        if (validationError) {
            this.setOrRemoveValidationError(_fieldName, validationError)
        } else {
            this.setOrRemoveValidationError(_fieldName, null)
        }
    }

    setOrRemoveValidationError(_divId: string, error: string | null) {
        this.setState(prevState => {
            let newErrors = prevState.validationErrors.filter(e => e.divId !== _divId); //remove any existing errors
            if (error) { // add a new error if existing
                newErrors.push({ divId: _divId, errorMsg: error });
            }
            return { ...prevState, validationErrors: newErrors }
        })
    }

    handleEdit(e: React.FormEvent<HTMLInputElement>, _fieldName: string) {
        const input = e.currentTarget;
        // keep numbers numbers, an empty number field is NaN
        const newValue = input.type === 'number' ? (input.value === '' ? NaN : Number(input.value)) : input.value;
        this.setValueOnState(newValue, _fieldName);
    }

    handleCheckBoxEdit(e: React.FormEvent<HTMLInputElement>, _fieldName: string) {
        const newValue = e.currentTarget.checked;
        this.setValueOnState(newValue, _fieldName);
    }

    handleDateEdit(date: Date | [Date, Date] | null, _fieldName: string) {
        if (date instanceof Date) {
            this.setValueOnState(date, _fieldName);
        }
    }

    handleSelect(selectedOption: SelectedOption, _fieldName: string) {
        let fieldName = _fieldName.indexOf(".") > 0 ? _fieldName.substr(_fieldName.indexOf(".") + 1) : _fieldName;
        let prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
        this.setState(prevState => {
            // a field that started as null has no entry yet
            const newSelectedOption = [...prevState.selectedOptions.filter(ps => ps.divId !== selectedOption.divId), selectedOption];
            const selectObject = this.getOriginalObjectFromStore(this.storeKeyForSelect(_fieldName, fieldName), selectedOption);
            let newFormObject = Object.assign({}, prevState.formObject);
            if (prefix) { // name refers to a sublevel, retrieve it
                newFormObject = this.getValueFromObj(newFormObject, prefix) as object;
            }
            newFormObject = this.setValueOnObject(newFormObject, selectObject!, fieldName);
            if (prefix) { // name refers to a sublevel, retrieve it
                newFormObject = this.setValueOnObject(prevState.formObject!, newFormObject, prefix);
            }
            if (this.props.inheritances && this.props.inheritances.length > 0) {
                newFormObject = this.handleInheritance(newFormObject, _fieldName);
            }
            // a selection can rule out another one, e.g. a maintenance of a different machine type
            const stale = newSelectedOption.filter(so => so.value?.id && so.divId.indexOf(".") < 0
                && this.props.selectConfigs?.find(sc => sc.divId === so.divId)?.filter?.(so.value, newFormObject) === false);
            stale.forEach(so => newFormObject = this.setValueOnObject(newFormObject, null, so.divId));
            return { selectedOptions: newSelectedOption.filter(so => !stale.includes(so)), formObject: newFormObject }
        });
    }

    handleClearSelect(_fieldName: string) {
        this.setState(prevState => ({ selectedOptions: prevState.selectedOptions.filter(ps => ps.divId !== _fieldName) }));
        this.setValueOnState(null, _fieldName);
    }

    handleInheritance(fo: object, fieldName: string) {
        var inh = this.props.inheritances!.find(i => i.trigger === fieldName)
        if (inh) {
            const val = this.getValueFromFormObj(fo, inh.valueFrom);
            fo = this.setValueOnObject(fo, val as object, inh.label);
        }
        return fo;
    }

    handleCheckSelect(divId: string, e: React.FormEvent<HTMLInputElement>) {
        const cbVal = divId + "." + e.currentTarget.value;
        this.setState(prevState => {
            let newselectedCheckBoxes = [...prevState.selectedCheckBoxes];
            if (newselectedCheckBoxes.find(cb => cb === cbVal)) {
                newselectedCheckBoxes = newselectedCheckBoxes.filter(cb => cb !== cbVal);
            } else {
                newselectedCheckBoxes.push(cbVal)
            }
            return { ...prevState, selectedCheckBoxes: newselectedCheckBoxes }
        })
    }

    optionLabel(divId: string, o: BasicDtaObject) {
        return this.props.selectConfigs?.find(sc => sc.divId === divId)?.label?.(o) ?? o.name;
    }

    storeKeyForSelect(divId: string, model: string) {
        return this.props.selectConfigs?.find(sc => sc.divId === divId)?.storeKey ?? (model + 's'); // looking for arrays, so plural
    }

    getOriginalObjectFromStore(storeKey: string, selectedOption: SelectedOption) {
        for (const [key, value] of Object.entries(this.props.store)) {
            if (key === storeKey) {
                const list = value as BasicDtaObject[];
                return list.find(obj => obj.id === selectedOption.value!.id)
            }
        }
    }

    filterArchived(o: object) {
        for (const [key, value] of Object.entries(o)) {
            if (key === "archivedDate") {
                if (value)
                    return false;
                else
                    return true; // only one archived date per object
            }
        }
        return true;
    }

    addSelectComponentForObject(_model: string, required: boolean): React.JSX.Element {
        let model = _model;
        if (_model.indexOf(".") > 0) {
            model = _model.substr(_model.indexOf(".") + 1)
        }
        //check if that object is existing on the store
        let options: SelectedOption[] = [];
        let validOptions = false;
        // options = this.checkConstants(_model, model); // this might be a thing sometimes but not now
        // if (options.length > 0) {
        //     validOptions = true;
        // }
        if (!validOptions) {
            for (const [key, value] of Object.entries(this.props.store)) {
                if (key === this.storeKeyForSelect(_model, model)) {
                    const list = value as BasicDtaObject[];
                    const selectFilter = this.props.selectConfigs?.find(sc => sc.divId === _model)?.filter;
                    options = list.filter(e => this.filterArchived(e) && (!selectFilter || selectFilter(e, this.state.formObject!))).map(element => {
                        return {
                            divId: _model,
                            value: element,
                            label: this.optionLabel(_model, element)
                        }
                    })
                    validOptions = true;
                }
            }
        }
        if (validOptions) { // only create an element if there is a chance for options...
            // null, not undefined: react-select keeps showing its own last value when undefined
            const selVal = this.state.selectedOptions.find(so => so.divId === _model && !!so.value?.id) ?? null; // ids may be strings (users)
            return (
                <>
                    <Select
                        value={selVal}
                        onChange={selectableOption => {
                            if (selectableOption)
                                this.handleSelect(selectableOption, _model);
                            else
                                this.handleClearSelect(_model);
                        }}
                        isClearable={!required}
                        // let the wheel reach the scrolling modal while the menu is open
                        captureMenuScroll={false}
                        options={options}
                        placeholder={Translate.text(this.props.store.translations, "Select...")}
                        noOptionsMessage={() => Translate.text(this.props.store.translations, "No options")}
                        required={required}
                        className={"c-form__select"}
                        classNamePrefix="c-select"
                    />
                    {required &&
                        <input
                            tabIndex={-1}
                            autoComplete="off"
                            className="c-form__select c-form__select--input-required"
                            value={selVal?.value?.id}
                            onChange={() => { }}
                            required={required}
                        />
                    }
                </>
            )
        }
        return <></>
    }

    // checkConstants(divid: string, model: string) {
    //     let options: SelectedOption[] = [];
    // switch (model) {
    //     case "usages": {
    //         for (let item in UnitUsage) {
    //             if (isNaN(Number(item))) {
    //                 const bDo: BasicDtaObject = {id: Number(item), name: item}
    //                 options.push({divId: divid, value: bDo, label: item})
    //             }
    //         }
    //     }
    // }
    //     return options;
    // }

    addCheckboxComponentForObject(divId: string, fo: BasicDtaObject): React.JSX.Element {
        const errors = this.state.validationErrors && this.state.validationErrors.find(e => e.divId === divId)
        return (
            // todo, add some content, handle select deselect and persist to state.
            <div className={'c-form__control' + (errors ? ' c-form__control--error' : '')} >
                <input type="checkbox"
                    value={fo.id}
                    id={divId}
                    onChange={(e) => this.handleCheckSelect(divId, e)}
                    checked={!!this.state.selectedCheckBoxes.find(cb => cb === divId + "." + fo.id)}
                />
                {this.displayObject(fo)}
            </div>
        )
    }

    filterReservedFieldNames(name: string) {
        if (name === 'id' || name === 'archivedDate' || name === 'checked')
            return false;
        if (name.endsWith('.id'))
            return false;
        if (name.endsWith('Id'))
            return false;
        return true;
    }

    displayObject(formObj: object): React.JSX.Element {
        let formElements = this.getFormElements(formObj);
        return (
            <>
                {formElements.filter(fe => this.filterReservedFieldNames(fe.label) && !this.props.hidden?.includes(fe.label)).map(fe => {
                    const labelText = fe.label;
                    const feLabel = fe.label;
                    const errors = this.state.validationErrors && this.state.validationErrors.find(e => e.divId === fe.label)
                    return (
                        <React.Fragment key={feLabel}>
                            {fe.type !== 'object' && fe.type !== 'array' && fe.type !== 'radioArray' &&
                                <div className={'c-form__group' + (errors ? ' c-form__group--error' : '')}>
                                    <label className='c-form__label' htmlFor={feLabel}><Translator text={labelText} /></label>
                                    <div className={'c-form__control'}>{fe.value as React.ReactNode}</div>
                                </div>
                            }
                            {fe.type === 'object' &&
                                <>
                                    {this.displayObject(fe.value as object)}
                                </>
                            }
                        </React.Fragment>
                    )
                })}
            </>
        )
    }

    displayValue(value: unknown) {
        return value === null || value === undefined || (typeof value === 'number' && isNaN(value)) ? '' : value as string | number;
    }

    addLeadingZero(str: string) {
        if (str.length === 2)
            return str;
        return ('0' + str);
    }

    convertDate(dt: Date) {
        let cvtDt: string =
            dt.getFullYear() + '-' +
            this.addLeadingZero((dt.getMonth() + 1).toString()) + '-' +
            this.addLeadingZero(dt.getDate().toString()) + 'T' +
            this.addLeadingZero(dt.getHours().toString()) + ':' +
            this.addLeadingZero(dt.getMinutes().toString()) + ':' +
            this.addLeadingZero(dt.getSeconds().toString());
        return cvtDt;
    }

    getValueFromState(_fieldName: string) {
        let obj = this.state.formObject!;
        return this.getValueFromFormObj(obj, _fieldName);
    }

    getValueFromObj(obj: object | Array<object>, fieldName: string, idx?: number): string | number | Date | object | boolean {
        let result: string | number | Date | object | boolean = '';
        for (const [key, value] of Object.entries(obj)) {
            if (key === fieldName)
                if (idx !== undefined && idx >= 0) {
                    result = (value as Array<object>)[idx];
                } else {
                    result = value;
                }
        }
        return result;
    }

    getValueFromFormObj(obj: object | Array<object>, _fieldName: string) {
        let fieldName = "";
        let prefix = ""
        let idx = -1;
        if ((_fieldName.match(/\./g) || []).length === 2) {
            prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
            _fieldName = _fieldName.indexOf(".") > 0 ? _fieldName.substr(_fieldName.indexOf(".") + 1) : "";
            obj = this.getValueFromObj(obj, prefix, idx) as object
        }
        if ((_fieldName.match(/\./g) || []).length < 2) {
            fieldName = _fieldName.indexOf(".") > 0 ? _fieldName.substr(_fieldName.indexOf(".") + 1) : _fieldName;
            prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
        } else {
            prefix = _fieldName.indexOf(".") > 0 ? _fieldName.substr(0, _fieldName.indexOf(".")) : "";
            let len = prefix.length + 1;
            idx = parseInt(_fieldName.indexOf(".", len) > 0 ? _fieldName.substr(len, _fieldName.indexOf(".", len)) : "-1");
            let len2 = _fieldName.indexOf(".", len);
            fieldName = _fieldName.indexOf(".", len2) > 0 ? _fieldName.substr(_fieldName.indexOf(".", len2) + 1) : _fieldName;
        }
        if (prefix) { // name refers to a sublevel, retrieve it
            obj = this.getValueFromObj(obj, prefix, idx) as object;
            return this.getValueFromObj(obj, fieldName)
        }
        return this.getValueFromObj(obj, fieldName, idx);
    }

    setValueOnObject(obj: object, newValue: string | number | BasicDtaObject | object | Date | boolean | null, name: string, idx?: number) {
        let newObj = Object.assign({}, obj);
        for (const [key, value] of Object.entries(obj)) {
            if (key === name) {
                if (value instanceof Date) {
                    newValue = new Date(newValue as string)
                }
                if (idx !== undefined && idx >= 0) {
                    let newArray = [...value];
                    newArray[idx] = newValue;
                    newObj = { ...newObj, [key]: newArray };
                } else {
                    newObj = { ...newObj, [key]: newValue };
                }
            }
        }
        return newObj;
    }

    extendArrayOnState(label: string) {
        let arr = this.getValueFromState(label) as Array<object>;
        let clonedObj = Object.assign({}, arr[arr.length - 1]);
        for (const [key, value] of Object.entries(clonedObj)) {
            if (typeof value === "string") {
                clonedObj = { ...clonedObj, [key]: "" }
            }
            if (typeof value === "number") {
                clonedObj = { ...clonedObj, [key]: 0 }
            }
            if (value instanceof Date) {
                clonedObj = { ...clonedObj, [key]: new Date() }
            }
        }
        arr.push(clonedObj);
        this.setValueOnState(arr, label);
    }

    checkDependencies(divId: string, obj: object) {
        if (this.props.dependencies && this.props.dependencies.length > 0) {
            const decObj = this.getFormElements(obj);
            const dependencies = this.props.dependencies.filter(d => d.label.includes(divId))
            var results = dependencies.map(dependency => {
                const dependantValue = this.getValueFromState(dependency.dependentOn);
                if (dependencies && dependencies.length > 0) {
                    const compareAttr = decObj.filter(d => divId + "." + d.label === dependency.label);
                    if (compareAttr.length > 0) {
                        if (compareAttr[0].value === dependantValue)
                            return true;
                    }
                }
            })
            if (results.filter(r => r).length > 0) {
                return true
            }
            return false;
        }
        return true;
    }

    loadForm(formObj: object, prefix?: string, idx?: number): React.JSX.Element {
        // shortcut: the template only types top-level fields, nested null fields stay untyped
        let formElements = this.getFormElements(formObj, prefix ? undefined : this.props.template);
        return (
            <>
                {formElements.map(fe => {
                    const feLabel = prefix ? prefix + "." + (idx !== undefined ? idx + "." : "") + fe.label : (idx ? idx + "." : "") + fe.label;
                    const labelText = this.props.labels?.find(l => l.divId === feLabel)?.text(this.state.formObject!) ?? fe.label;
                    if (this.props.selectConfigs?.find(sc => sc.divId === feLabel)?.visible?.(this.state.formObject!) === false) {
                        return null;
                    }
                    const errors = this.state.validationErrors && this.state.validationErrors.find(e => e.divId === feLabel)
                    const tooltip = this.props.toolTips && this.props.toolTips.find(t => t.divId === feLabel);
                    let arr: Array<object> = [];
                    let thisInputType: typeof InputTypes.basic;
                    switch (fe.type) {
                        case 'boolean':
                            thisInputType = InputTypes.checkbox;
                            break;
                        case 'date':
                            thisInputType = InputTypes.date;
                            break;
                        case 'object':
                            if (this.props.nonSelectables && this.props.nonSelectables?.includes(feLabel)) {
                                thisInputType = InputTypes.newObject;
                                fe.required = false;
                            }
                            else {
                                thisInputType = InputTypes.select;
                            }
                            break;
                        case 'array':
                        case 'radioArray':
                            thisInputType = fe.type;
                            arr = (this.getValueFromState(feLabel) as Array<object>).filter(ar => this.checkDependencies(feLabel, ar));
                            fe.required = false;
                            break;
                        default:
                            thisInputType = InputTypes.basic;
                            break;
                    }
                    return (
                        <React.Fragment key={feLabel}>
                            {(thisInputType === InputTypes.array || thisInputType === InputTypes.radioArray) && (!arr || arr.length <= 0) ? <></> :
                                <>
                                    <div className={((thisInputType === InputTypes.newObject || thisInputType === InputTypes.array || thisInputType === InputTypes.radioArray) ? 'c-form__sub-form' : 'c-form__group') + (errors ? ' c-form__group--error' : '') + (tooltip ? ' c-form__group--tooltip' : '')}>
                                        {this.filterReservedFieldNames(feLabel) && !this.props.hidden?.includes(feLabel) && fe.label != 'archivedDate' &&
                                            <label className={'c-form__label' + (fe.required ? ' c-form__label--required' : '')} htmlFor={feLabel}><Translator text={labelText} /></label>
                                        }
                                        {thisInputType === InputTypes.basic &&
                                            <input className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                id={feLabel}
                                                required={fe.required}
                                                disabled={this.props.protected?.includes(feLabel)}
                                                onChange={(e) => this.handleEdit(e, feLabel)}
                                                onBlur={(e) => this.onExit(e, feLabel)}
                                                type={this.inputTypeForField(fe.type.toString())}
                                                step={fe.type === 'number' ? 'any' : undefined}
                                                value={this.displayValue(this.getValueFromState(feLabel))}
                                                hidden={feLabel === 'id' || feLabel.endsWith('Id') || feLabel.endsWith('.id') || this.props.hidden?.includes(feLabel)}
                                                autoComplete="off"
                                            />
                                        }
                                        {thisInputType === InputTypes.checkbox &&
                                            <input className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                id={feLabel}
                                                required={fe.required}
                                                disabled={this.props.protected?.includes(feLabel)}
                                                onChange={(e) => this.handleCheckBoxEdit(e, feLabel)}
                                                onBlur={(e) => this.onExit(e, feLabel)}
                                                type={thisInputType}
                                                checked={this.getValueFromState(feLabel) as boolean}
                                                value={feLabel}
                                                hidden={feLabel === 'id' || feLabel.endsWith('Id') || feLabel.endsWith('.id') || this.props.hidden?.includes(feLabel)}
                                                autoComplete="off"
                                            />
                                        }
                                        {thisInputType === InputTypes.date &&
                                            <ReactDatePicker className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                id={feLabel}
                                                onChange={(e: Date | null) => this.handleDateEdit(e, feLabel)}
                                                required={fe.required}
                                                selected={this.getValueFromState(feLabel) as Date}
                                                dateFormat="dd.MM.yyyy hh:mm"
                                                showTimeSelect
                                                locale="de" // todo, make this react to set language
                                                timeCaption={Translate.text(this.props.store.translations, "Time")}
                                                timeFormat="HH:mm"
                                            />
                                        }
                                        {thisInputType === InputTypes.select &&
                                            <>
                                                <input className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                    id={feLabel}
                                                    type='object'
                                                    hidden
                                                />
                                                {this.addSelectComponentForObject(feLabel, fe.required)}
                                            </>
                                        }
                                        {thisInputType === InputTypes.newObject &&
                                            <>
                                                <input className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                    id={feLabel}
                                                    type='object'
                                                    hidden
                                                />
                                                {this.loadForm(fe.value as object, feLabel)}
                                            </>
                                        }
                                        {(thisInputType === InputTypes.array || thisInputType === InputTypes.radioArray) && arr && arr.length > 0 &&
                                            <>
                                                <input className={'c-form__control' + (errors ? ' c-form__control--error' : '')}
                                                    id={feLabel}
                                                    type='object'
                                                    hidden
                                                />
                                                {thisInputType === InputTypes.array &&
                                                    arr.map((ar, idx) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                {this.loadForm(ar, feLabel, idx)}
                                                            </React.Fragment>
                                                        )
                                                    })
                                                }
                                                {thisInputType === InputTypes.radioArray &&
                                                    arr.map((ar, idx) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                {this.addCheckboxComponentForObject(feLabel, ar as BasicDtaObject)}
                                                            </React.Fragment>
                                                        )
                                                    })
                                                }
                                                {thisInputType === InputTypes.array &&
                                                    <button className='c-button' type='button' onClick={() => this.extendArrayOnState(feLabel)}>{<Translator text="More" />}</button>
                                                }
                                            </>
                                        }
                                    </div>
                                    {errors &&
                                        <div className='c-form__validation'>
                                            {this.state.validationErrors.find(e => e.divId === feLabel)?.errorMsg}
                                        </div>
                                    }
                                    {!errors && tooltip &&
                                        <div className='c-form__tooltip'>
                                            <Translator text={tooltip.text} />
                                        </div>
                                    }
                                </>}
                        </React.Fragment>
                    )
                })}
            </>
        )
    }

    render() {
        const registerOrNew = this.props.store.router && (
            this.props.store.router.location?.pathname.toUpperCase() === "/ACCOUNT/REGISTER"
            || this.props.store.router.location?.pathname.toUpperCase().startsWith("/ACCOUNT/SETNEWPASSWORD/")
        );
        const buttonText = registerOrNew ? "Register" : (this.loggedIn() ? "Save" : "Login");
        const processing = this.props.store.processingRequest;
        return (
            <>
                {this.props.formObject &&
                    <form className="c-form" onSubmit={(e) => this.performCallback(e)}>
                        <div>
                            {this.loadForm(this.props.formObject)}
                            <div className='c-form__group'>
                                <button className='c-button c-button--primary' type='submit' disabled={processing}>
                                    <>
                                        {!processing &&
                                            <Translator text={buttonText} />
                                        }
                                        {processing &&
                                            <div className="c-button__loader" />
                                        }
                                    </>
                                </button>
                            </div>
                        </div>
                    </form >
                }
            </>
        );
    }
}
